import { useState, useCallback, useEffect } from 'react';
import { Transaction } from '@/types/transaction';
import { filterTransactions } from '@/utils/transactionFilters';

interface UseTransactionFiltersOptions {
  transactions: Transaction[];
}

interface UseTransactionFiltersReturn {
  filteredTransactions: Transaction[];
  paymentMethod: string | null;
  keepMoreStatus: string | null;
  minAmount: number | undefined;
  filterByStatus: (status?: string) => void;
  filterByPaymentMethod: (method: string | null) => void;
  filterByKeepMore: (isKeepMore: string | null) => void;
  filterByMinAmount: (amount: number | undefined) => void;
}

export function useTransactionFilters({ 
  transactions 
}: UseTransactionFiltersOptions): UseTransactionFiltersReturn {
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  const [keepMoreStatus, setKeepMoreStatus] = useState<string | null>(null);
  const [minAmount, setMinAmount] = useState<number | undefined>(undefined);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>(transactions);

  // Apply filters whenever transactions or filter criteria change
  useEffect(() => {
    // Add proper logging to track filter application
    
    const filteredResults = filterTransactions(
      transactions,
      paymentMethod,
      keepMoreStatus,
      minAmount
    );
    
    setFilteredTransactions(filteredResults);

  }, [transactions, paymentMethod, keepMoreStatus, minAmount]);

  const filterByStatus = useCallback((newStatus?: string) => {
    setStatus(newStatus);
  }, []);

  const filterByPaymentMethod = useCallback((method: string | null) => {
    setPaymentMethod(method);
  }, []);

  const filterByKeepMore = useCallback((isKeepMore: string | null) => {
    setKeepMoreStatus(isKeepMore);
  }, []);

  const filterByMinAmount = useCallback((amount: number | undefined) => {
    if (amount !== undefined) {
      // Keep amount in dollars, don't convert to cents here
      console.log(`Filtering by minimum amount: $${amount.toFixed(2)}`);
      setMinAmount(amount);
    } else {
      setMinAmount(undefined);
    }
  }, []);

  return {
    filteredTransactions,
    paymentMethod,
    keepMoreStatus,
    minAmount,
    filterByStatus,
    filterByPaymentMethod,
    filterByKeepMore,
    filterByMinAmount
  };
}
